import axiosInstance from "./axiosConfig";
import { handleAPIError } from "./utils/errors";
import {
  AdminListLawFirmsParams,
  AdminListLawFirmsResponse,
  CreateLawFirmLawyerRequest,
  CreateLawFirmInviteRequest,
  CreateLawFirmRequest,
  CreateLawFirmSubscriptionRequest,
  CreateLawFirmSubscriptionResponse,
  LawFirm,
  LawFirmInviteDTO,
  LawyerDTO,
  Subscription,
  SubscriptionWithPlanDTO,
  UpdateSubscriptionRequest,
  UpdateLawFirmRequest,
  SsoSetupStateDTO,
  CreateTenantResponseDTO,
  FirmAgentInstructionsDTO,
  FirmPermissionMatrix,
  FirmPermissionsDTO,
  UpdateSsoAllowedEmailDomainsRequestDTO,
  UpsertSsoProviderRequestDTO,
  ProvisionUsersRequestDTO,
  ProvisionUsersResponseDTO,
  ProviderConfigsResponseDTO,
  SamlMetadataImportRequestDTO,
  SamlMetadataImportResponseDTO,
  ListLawFirmForeignNationalsParams,
  ListLawFirmForeignNationalsResponse,
  ValidateSsoProviderRequestDTO,
  ValidateSsoProviderResponseDTO
} from "./types/lawFirm";

const BASE_URL = "/law_firms";
const ADMIN_BASE_URL = "/admin/law_firms";

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const normalizeLawFirm = (raw: unknown): LawFirm => {
  if (!isRecord(raw)) return raw as LawFirm;

  const latestSubscription = (raw.latest_subscription ??
    raw.subscription ??
    null) as LawFirm["subscription"];
  const latestPlan = (raw.latest_subscription_plan ??
    raw.subscription_plan ??
    null) as LawFirm["subscription_plan"];

  const subscription_status =
    (raw.subscription_status as LawFirm["subscription_status"]) ??
    (isRecord(latestSubscription)
      ? (latestSubscription.subscription_status as LawFirm["subscription_status"])
      : null) ??
    null;

  const subscription_plan_type =
    (raw.subscription_plan_type as LawFirm["subscription_plan_type"]) ??
    (isRecord(latestPlan)
      ? (latestPlan.subscription_plan_type as LawFirm["subscription_plan_type"])
      : null) ??
    null;

  return {
    ...(raw as unknown as LawFirm),
    latest_subscription:
      (raw.latest_subscription as LawFirm["latest_subscription"]) ?? null,
    latest_subscription_plan:
      (raw.latest_subscription_plan as LawFirm["latest_subscription_plan"]) ??
      null,
    subscription: latestSubscription,
    subscription_plan: latestPlan,
    subscription_status,
    subscription_plan_type
  };
};

const normalizeAdminListResponse = (
  payload: LawFirm[] | AdminListLawFirmsResponse
): AdminListLawFirmsResponse => {
  if (Array.isArray(payload)) {
    return {
      lawFirms: payload.map(normalizeLawFirm),
      nextPageToken: null,
      totalCount: payload.length
    };
  }
  return {
    lawFirms: (payload.lawFirms ?? []).map(normalizeLawFirm),
    nextPageToken: payload.nextPageToken ?? null,
    totalCount:
      typeof payload.totalCount === "number" ? payload.totalCount : undefined
  };
};

export const LawFirmsAPI = {
  async adminList(
    params?: AdminListLawFirmsParams
  ): Promise<AdminListLawFirmsResponse> {
    try {
      const response = await axiosInstance.get(ADMIN_BASE_URL, { params });
      return normalizeAdminListResponse(response.data);
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async create(data: CreateLawFirmRequest): Promise<LawFirm> {
    try {
      const response = await axiosInstance.post(BASE_URL, data);
      return normalizeLawFirm(response.data);
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getById(lawFirmId: string): Promise<LawFirm> {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/${lawFirmId}`);
      return normalizeLawFirm(response.data);
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listChildren(lawFirmId: string): Promise<LawFirm[]> {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/${lawFirmId}/children`
      );
      const payload = response.data as unknown;
      if (Array.isArray(payload)) {
        return payload.map(normalizeLawFirm);
      }
      return [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listDescendants(lawFirmId: string): Promise<LawFirm[]> {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/${lawFirmId}/descendants`
      );
      const payload = response.data as unknown;
      if (Array.isArray(payload)) {
        return payload.map(normalizeLawFirm);
      }
      return [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async update(
    lawFirmId: string,
    data: UpdateLawFirmRequest
  ): Promise<LawFirm> {
    try {
      const response = await axiosInstance.put(
        `${BASE_URL}/${lawFirmId}`,
        data
      );
      return normalizeLawFirm(response.data);
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Puts the firm on or off the Classic drafting allowlist (superadmin only). */
  async setLegacyDrafting(
    lawFirmId: string,
    enabled: boolean
  ): Promise<{
    legacy_drafting_enabled: boolean;
    legacy_drafting_enabled_at?: string;
  }> {
    try {
      const response = await axiosInstance.put(
        `/admin/law-firms/${lawFirmId}/legacy-drafting`,
        { enabled }
      );
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  async uploadLogo(lawFirmId: string, file: File): Promise<{ success: true }> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axiosInstance.post(
        `${BASE_URL}/${lawFirmId}/logo`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async delete(lawFirmId: string): Promise<void> {
    try {
      await axiosInstance.delete(`${BASE_URL}/${lawFirmId}`);
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createLawyer(
    lawFirmId: string,
    data: CreateLawFirmLawyerRequest
  ): Promise<LawyerDTO> {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/${lawFirmId}/lawyers`,
        data
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createInvite(
    lawFirmId: string,
    data: CreateLawFirmInviteRequest
  ): Promise<LawFirmInviteDTO> {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/${lawFirmId}/invites`,
        data
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** The firm's standing instructions for its agents; any member may read. */
  async getAgentInstructions(
    lawFirmId: string
  ): Promise<FirmAgentInstructionsDTO> {
    try {
      const response = await axiosInstance.get<FirmAgentInstructionsDTO>(
        `${BASE_URL}/${lawFirmId}/agents-md`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /**
   * Replace the firm's standing instructions (owners and admins; blank clears).
   * `baseVersion` is the version the editor loaded; the server answers 409 if
   * the text changed since.
   */
  async updateAgentInstructions(
    lawFirmId: string,
    text: string,
    baseVersion?: string
  ): Promise<FirmAgentInstructionsDTO> {
    try {
      const response = await axiosInstance.put<FirmAgentInstructionsDTO>(
        `${BASE_URL}/${lawFirmId}/agents-md`,
        baseVersion === undefined
          ? { text }
          : { text, base_version: baseVersion }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Who may write Firm Knowledge, and what the caller may do; any member may read. */
  async getPermissions(lawFirmId: string): Promise<FirmPermissionsDTO> {
    try {
      const response = await axiosInstance.get<FirmPermissionsDTO>(
        `${BASE_URL}/${lawFirmId}/permissions`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  /** Change who may write Firm Knowledge (owners and admins; any subset of the grid). */
  async updatePermissions(
    lawFirmId: string,
    matrix: Partial<{
      [K in keyof FirmPermissionMatrix]: Partial<FirmPermissionMatrix[K]>;
    }>
  ): Promise<FirmPermissionsDTO> {
    try {
      const response = await axiosInstance.put<FirmPermissionsDTO>(
        `${BASE_URL}/${lawFirmId}/permissions`,
        { matrix }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getSsoSetup(lawFirmId: string): Promise<SsoSetupStateDTO> {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/${lawFirmId}/sso_setup`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createTenant(
    lawFirmId: string,
    tenantId?: string | null
  ): Promise<CreateTenantResponseDTO> {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/${lawFirmId}/sso_setup/tenant`,
        { tenantId: tenantId ?? undefined }
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async updateSsoAllowedEmailDomains(
    lawFirmId: string,
    data: UpdateSsoAllowedEmailDomainsRequestDTO
  ): Promise<SsoSetupStateDTO> {
    try {
      const response = await axiosInstance.put(
        `${BASE_URL}/${lawFirmId}/sso_setup/domains`,
        data
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async upsertSsoProvider(
    lawFirmId: string,
    data: UpsertSsoProviderRequestDTO
  ): Promise<{ status: "ok" }> {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/${lawFirmId}/sso_setup/provider`,
        data
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async provisionSsoUsers(
    lawFirmId: string,
    data: ProvisionUsersRequestDTO
  ): Promise<ProvisionUsersResponseDTO> {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/${lawFirmId}/sso_setup/provision_users`,
        data
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async enableSso(lawFirmId: string): Promise<{ status: "ok" }> {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/${lawFirmId}/sso_setup/enable`,
        {}
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async getSsoProviderConfigs(
    lawFirmId: string
  ): Promise<ProviderConfigsResponseDTO> {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/${lawFirmId}/sso_setup/provider_configs`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async importSamlMetadata(
    lawFirmId: string,
    data: SamlMetadataImportRequestDTO
  ): Promise<SamlMetadataImportResponseDTO> {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/${lawFirmId}/sso_setup/saml/metadata_import`,
        data
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async validateSsoProvider(
    lawFirmId: string,
    data: ValidateSsoProviderRequestDTO
  ): Promise<ValidateSsoProviderResponseDTO> {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/${lawFirmId}/sso_setup/validate_provider`,
        data
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listLawyers(lawFirmId: string): Promise<LawyerDTO[]> {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/${lawFirmId}/lawyers`
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listSubscriptions(
    lawFirmId: string
  ): Promise<SubscriptionWithPlanDTO[]> {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/${lawFirmId}/subscriptions`
      );
      const payload = response.data as unknown;
      if (
        Array.isArray(payload) &&
        payload.every(
          (x) =>
            x &&
            typeof x === "object" &&
            "subscription" in (x as Record<string, unknown>)
        )
      ) {
        return payload as SubscriptionWithPlanDTO[];
      }
      // Backwards compatibility (older API returned SubscriptionDTO[])
      if (Array.isArray(payload)) {
        return (payload as Subscription[]).map((subscription) => ({
          subscription,
          subscription_plan: null
        }));
      }
      return [];
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async listForeignNationals(
    lawFirmId: string,
    params: ListLawFirmForeignNationalsParams = {}
  ): Promise<ListLawFirmForeignNationalsResponse> {
    try {
      const response =
        await axiosInstance.get<ListLawFirmForeignNationalsResponse>(
          `${BASE_URL}/${lawFirmId}/foreign_nationals`,
          { params }
        );
      const raw = response.data;
      return {
        foreign_nationals: Array.isArray(raw?.foreign_nationals)
          ? raw.foreign_nationals
          : [],
        total_count: Number.isFinite(raw?.total_count) ? raw.total_count : 0,
        assigned_count: Number.isFinite(raw?.assigned_count)
          ? raw.assigned_count
          : 0
      };
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async createSubscription(
    lawFirmId: string,
    data: CreateLawFirmSubscriptionRequest
  ): Promise<CreateLawFirmSubscriptionResponse> {
    try {
      const response = await axiosInstance.post(
        `${BASE_URL}/${lawFirmId}/subscriptions`,
        data
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  },

  async updateSubscription(
    subscriptionId: string,
    data: UpdateSubscriptionRequest
  ): Promise<Subscription> {
    try {
      const response = await axiosInstance.put(
        `/subscriptions/${subscriptionId}`,
        data
      );
      return response.data;
    } catch (error) {
      const apiError = handleAPIError(error);
      throw apiError;
    }
  }
};
